import { trackAdEvent } from '@/lib/adPixels';
import { useCart } from '@/contexts/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Minus, Plus, Trash2, ShoppingBag, Loader2, MapPin, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from "@/components/shared/PageHeader";
import { DeliveryOptions } from '@/components/checkout/DeliveryOptions';
import { useVatRate } from '@/hooks/useVatRate';
import { StateCitySelect } from '@/components/shared/StateCitySelect';
import { PaymentMethodSelector } from '@/components/checkout/PaymentMethodSelector';
import { placePayOnDeliveryOrder, type PaymentMethod } from '@/services/payOnDelivery';

interface DeliveryRate {
  id: string;
  name: string;
  type: 'regular' | 'express' | 'faramove';
  price: number;
  currency: string;
  estimatedDays: string;
  description: string;
  isSelfDelivery?: boolean;
  provider?: 'fez' | 'self';
}

export default function Cart() {
  const { items, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { vatRate, vatEnabled, calculateVat } = useVatRate();
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('online');
  
  // Debug logging
  console.log('Cart page rendered, items:', items.length, items);
  
  // Delivery address state
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<DeliveryRate | null>(null);
  
  // Guest auto-account creation
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  // Seller state for delivery calculation
  const [sellerState, setSellerState] = useState('Lagos');
  const [resolvedSellerIds, setResolvedSellerIds] = useState<Record<string, string>>({});

  const isValidUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const resolveItemSellerId = async (item: (typeof items)[number]) => {
    const normalizedSellerId = (item.sellerId || '').trim();
    if (isValidUuid(normalizedSellerId)) {
      return normalizedSellerId;
    }

    if (!isValidUuid(item.id)) {
      return '';
    }

    const { data, error } = await supabase.rpc('get_public_product_by_id', {
      p_product_id: item.id,
    });

    if (error) {
      console.error('Failed to resolve seller from product:', { productId: item.id, error });
      return '';
    }

    const resolvedSellerId = Array.isArray(data) ? data[0]?.seller_id : undefined;
    return typeof resolvedSellerId === 'string' && isValidUuid(resolvedSellerId) ? resolvedSellerId : '';
  };

  const getResolvedSellerIdForItem = (item: (typeof items)[number]) => {
    const directSellerId = (item.sellerId || '').trim();
    if (isValidUuid(directSellerId)) {
      return directSellerId;
    }
    return resolvedSellerIds[item.id] || '';
  };

  const sellerGroups = useMemo(() => {
    return items.reduce((acc, item) => {
      const sellerId = getResolvedSellerIdForItem(item);
      if (!sellerId) return acc;

      if (!acc[sellerId]) {
        acc[sellerId] = [];
      }
      acc[sellerId].push(item);
      return acc;
    }, {} as Record<string, typeof items>);
  }, [items, resolvedSellerIds]);

  const sellerIds = useMemo(() => Object.keys(sellerGroups), [sellerGroups]);
  const hasMixedSellers = sellerIds.length > 1;

  const checkoutSellerId = useMemo(() => sellerIds[0] || '', [sellerIds]);

  const checkoutItems = useMemo(() => items, [items]);

  const unresolvedSellerItemsCount = useMemo(() => {
    return items.filter((item) => !getResolvedSellerIdForItem(item)).length;
  }, [items, resolvedSellerIds]);

  useEffect(() => {
    let isCancelled = false;

    const resolveMissingSellerIds = async () => {
      const entries = await Promise.all(
        items.map(async (item) => {
          const sellerId = await resolveItemSellerId(item);
          return { productId: item.id, sellerId };
        })
      );

      if (isCancelled) return;

      const updates: Record<string, string> = {};
      for (const entry of entries) {
        if (entry.sellerId) {
          updates[entry.productId] = entry.sellerId;
        }
      }

      setResolvedSellerIds((prev) => {
        const currentProductIds = new Set(items.map((item) => item.id));
        const next = Object.fromEntries(
          Object.entries(prev).filter(([productId]) => currentProductIds.has(productId))
        );
        return { ...next, ...updates };
      });
    };

    resolveMissingSellerIds();

    return () => {
      isCancelled = true;
    };
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps


  // Reset delivery option when location or checkout seller changes to force recalculation
  useEffect(() => {
    if (deliveryState || deliveryCity || checkoutSellerId) {
      console.log('Checkout context changed, resetting delivery option:', {
        deliveryCity,
        deliveryState,
        checkoutSellerId,
      });
      setSelectedDeliveryOption(null);
    }
  }, [deliveryState, deliveryCity, checkoutSellerId]);

  // Fetch checkout seller's pickup state when checkoutSellerId changes
  useEffect(() => {
    const fetchSellerState = async () => {
      if (!checkoutSellerId) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('pickup_address_state')
          .eq('id', checkoutSellerId)
          .maybeSingle();

        if (!error && data?.pickup_address_state) {
          setSellerState(data.pickup_address_state);
        }
      } catch (err) {
        console.warn('Could not fetch seller state:', err);
      }
    };

    fetchSellerState();
  }, [checkoutSellerId]);

  // Check auth state and prefill recipient details
  useEffect(() => {
    const prefillFromProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
      if (!user) return;
      if (user.email) setGuestEmail(user.email);
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', user.id)
        .maybeSingle();
      if (profile) {
        if (profile.full_name && !recipientName) setRecipientName(profile.full_name);
        if (profile.phone && !recipientPhone) setRecipientPhone(profile.phone);
      }
    };
    prefillFromProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryFee = selectedDeliveryOption?.price || 0;
  const subtotal = checkoutItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const vat = vatEnabled ? calculateVat(subtotal) : 0; // VAT on cart subtotal
  const isPayOnDelivery = paymentMethod === 'pay_on_delivery';
  const transactionFee = isPayOnDelivery ? 0 : Math.round((subtotal + deliveryFee + vat) * 0.015); // 1.5% online transaction fee
  const grandTotal = subtotal + deliveryFee + vat + transactionFee;


  const handleCheckout = async () => {
    // Validate all 5 required delivery fields
    const missingFields = [];
    if (!recipientName.trim()) missingFields.push('recipient name');
    if (!recipientPhone.trim()) missingFields.push('phone number');
    if (!deliveryAddress.trim()) missingFields.push('street address');
    if (!deliveryCity) missingFields.push('city');
    if (!deliveryState) missingFields.push('state');

    if (missingFields.length > 0) {
      toast({
        title: "Please complete delivery information",
        description: `Missing: ${missingFields.join(', ')}`,
        variant: "destructive"
      });
      setShowDeliveryForm(true);
      return;
    }

    const phoneRegex = /^(\+234|0)[789]\d{9}$/;
    if (!phoneRegex.test(recipientPhone.trim())) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid Nigerian phone number",
        variant: "destructive"
      });
      return;
    }

    trackAdEvent('InitiateCheckout', {
      value: subtotal,
      content_ids: checkoutItems.map((i) => i.id),
      num_items: checkoutItems.reduce((n, i) => n + i.quantity, 0),
    });


    if (!selectedDeliveryOption) {
      toast({
        title: "Select Delivery Option",
        description: "Please select a delivery option to continue",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      // Get current user (optional for guest checkout)
      const { data: { user } } = await supabase.auth.getUser();
      
      // Resolve seller IDs per item first (items from Products page can have blank sellerId)
      const sellerByProductId = new Map<string, string>();
      const resolvedEntries = await Promise.all(
        items.map(async (item) => {
          let sellerId = sellerByProductId.get(item.id);
          if (!sellerId) {
            sellerId = await resolveItemSellerId(item);
            if (sellerId) sellerByProductId.set(item.id, sellerId);
          }
          return { item, sellerId: sellerId || '' };
        })
      );

      const unresolvedItems = resolvedEntries.filter((entry) => !entry.sellerId);
      if (unresolvedItems.length > 0) {
        throw new Error('Could not determine seller for one or more items. Please remove and re-add them, then try again.');
      }

      const itemsBySeller = resolvedEntries.reduce((acc, entry) => {
        if (!acc[entry.sellerId]) {
          acc[entry.sellerId] = [];
        }
        acc[entry.sellerId].push(entry.item);
        return acc;
      }, {} as Record<string, typeof items>);

      const availableSellerIds = Object.keys(itemsBySeller);
      if (availableSellerIds.length === 0) {
        throw new Error('Could not determine the seller for this order. Please try again.');
      }

      const resolvedSellerId = availableSellerIds[0];

      const orderItems = resolvedEntries.map(({ item }) => item);

      // Pay on Delivery: place order without online payment
      if (isPayOnDelivery) {
        const result = await placePayOnDeliveryOrder({
          items: orderItems.map((item) => ({ productId: item.id, quantity: item.quantity })),
          delivery_fee: deliveryFee,
          delivery_type: selectedDeliveryOption.provider === 'fez' ? 'fez' : 'self',
          recipient_name: recipientName.trim(),
          recipient_phone: recipientPhone.trim(),
          delivery_address_line: deliveryAddress.trim(),
          delivery_city: deliveryCity,
          delivery_state: deliveryState,
          email: user?.email || guestEmail.trim() || undefined,
        });
        items.forEach((item) => removeFromCart(item.id));
        navigate(`/order-placed?orderId=${result.orderId}&total=${Math.round(result.total)}`);
        return;
      }

      const itemsTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const itemsVat = vatEnabled ? calculateVat(itemsTotal) : 0;
      const itemsTransactionFee = Math.round((itemsTotal + deliveryFee + itemsVat) * 0.015);
      const totalWithDeliveryAndVat = itemsTotal + deliveryFee + itemsVat + itemsTransactionFee;
      
      // Get user email for payment
      let email = user?.email;
      if (!email) {
        email = guestEmail.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          toast({
            title: "Valid Email Required",
            description: "Please enter a valid email address",
            variant: "destructive"
          });
          setProcessing(false);
          return;
        }
        if (!guestPassword || guestPassword.length < 6) {
          toast({
            title: "Password Required",
            description: "Please enter a password (min 6 characters) to create your account",
            variant: "destructive"
          });
          setProcessing(false);
          return;
        }
        // Store guest credentials for auto-account creation after payment
        sessionStorage.setItem('guest_buyer_email', email);
        sessionStorage.setItem('guest_buyer_password', guestPassword);
        sessionStorage.setItem('pending_buyer_name', recipientName.trim());
        sessionStorage.setItem('pending_buyer_phone', recipientPhone.trim());
      }

      if (user?.id) {
        // Authenticated user: Use secure Edge Function with server-side price validation
        const { data, error } = await supabase.functions.invoke('create-authenticated-order', {
          body: {
            seller_id: resolvedSellerId,
            items: orderItems.map(item => ({
              productId: item.id,
              title: item.title,
              quantity: item.quantity
              // Price NOT sent - validated server-side for security
            })),
            delivery_fee: deliveryFee,
            delivery_type: selectedDeliveryOption.provider === 'fez' ? 'fez' : 'self',
            email: email,
            recipient_name: recipientName.trim(),
            recipient_phone: recipientPhone.trim(),
            delivery_address_line: deliveryAddress.trim(),
            delivery_city: deliveryCity,
            delivery_state: deliveryState,
            shipping_address: {
              fullName: recipientName.trim(),
              phone: recipientPhone.trim(),
              addressLine1: deliveryAddress.trim(),
              city: deliveryCity,
              state: deliveryState,
              country: 'Nigeria'
            }
          }
        });

        if (error) {
          // Extract actual error message from edge function response
          const errMsg = data?.error || error.message;
          throw new Error(errMsg);
        }

        if (data.authorization_url) {
          items.forEach((item) => removeFromCart(item.id));
          window.location.href = data.authorization_url;
        }
      } else {
        // Guest checkout: Use edge function to create order and initialize payment
        const { data, error } = await supabase.functions.invoke('create-guest-order', {
          body: {
            seller_id: resolvedSellerId,
            items: orderItems.map(item => ({
              productId: item.id,
              title: item.title,
              price: item.price,
              quantity: item.quantity
            })),
            total_amount: Math.round(totalWithDeliveryAndVat),
            delivery_fee: deliveryFee,
            delivery_type: selectedDeliveryOption.provider === 'fez' ? 'fez' : 'self',
            vat: Math.round(itemsVat),
            email: email,
            recipient_name: recipientName.trim(),
            recipient_phone: recipientPhone.trim(),
            delivery_address_line: deliveryAddress.trim(),
            delivery_city: deliveryCity,
            delivery_state: deliveryState,
            shipping_address: {
              fullName: recipientName.trim(),
              phone: recipientPhone.trim(),
              addressLine1: deliveryAddress.trim(),
              city: deliveryCity,
              state: deliveryState,
              country: 'Nigeria'
            }
          }
        });

        if (error) {
          const errMsg = data?.error || error.message;
          throw new Error(errMsg);
        }

        if (data.authorization_url) {
          items.forEach((item) => removeFromCart(item.id));
          window.location.href = data.authorization_url;
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMsg = error?.message || error?.context?.message || "Could not initialize payment. Please try again.";
      toast({
        title: "Checkout Failed",
        description: errorMsg,
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader
          title="Your Cart"
          description="Your cart is empty"
          backTo="/products"
        />
        <div className="flex flex-col items-center justify-center py-12">
          <ShoppingBag className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-muted-foreground mb-6">Add some products to your cart to see them here.</p>
          <Button onClick={() => navigate('/products')}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
      <PageHeader
        title="Your Cart"
        description={`${items.length} item${items.length !== 1 ? 's' : ''} in your cart`}
        backTo="/products"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Shopping Cart ({items.length} {items.length === 1 ? 'item' : 'items'})
                <Button variant="ghost" size="sm" onClick={clearCart}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item) => (
              <div key={item.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 sm:p-4 border rounded-lg">
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm sm:text-base line-clamp-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">₦{item.price.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between w-full sm:w-auto sm:gap-4">
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 sm:h-8 sm:w-8"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <Badge variant="secondary" className="px-2.5 text-sm">
                        {item.quantity}
                      </Badge>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 sm:h-8 sm:w-8"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm sm:text-base">₦{(item.price * item.quantity).toLocaleString()}</p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

{/* Mixed-seller banner removed */}

          {unresolvedSellerItemsCount > 0 && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Finalizing seller details for {unresolvedSellerItemsCount} item{unresolvedSellerItemsCount !== 1 ? 's' : ''}...
                </p>
              </CardContent>
            </Card>
          )}

          {/* Delivery Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Delivery Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              {false ? (
                <div />
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {/* Recipient Details */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="recipientName">Recipient Name *</Label>
                      <Input
                        id="recipientName"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="recipientPhone">Phone Number *</Label>
                      <Input
                        id="recipientPhone"
                        value={recipientPhone}
                        onChange={(e) => setRecipientPhone(e.target.value)}
                        placeholder="e.g., 08012345678"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <Label htmlFor="deliveryAddress">Street Address *</Label>
                    <Input
                      id="deliveryAddress"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Enter full street address"
                    />
                  </div>

                  <StateCitySelect
                    stateValue={deliveryState}
                    cityValue={deliveryCity}
                    onStateChange={setDeliveryState}
                    onCityChange={setDeliveryCity}
                    stateLabel="State"
                    cityLabel="City"
                  />
                  
                  {deliveryState && deliveryCity && (
                    <div className="pt-2">
                      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                        <MapPin className="h-3 w-3" />
                        {deliveryCity}, {deliveryState}
                      </Badge>
                    </div>
                  )}

                  {/* Guest Email & Password - inside delivery info */}
                  {isLoggedIn === false && (
                    <div className="space-y-3 pt-2 border-t">
                      <h4 className="font-medium text-sm flex items-center gap-2 pt-2">
                        Create Your Account
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Sign up to track your order and manage purchases
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="guestEmail">Email Address *</Label>
                          <Input
                            id="guestEmail"
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            placeholder="you@example.com"
                          />
                        </div>
                        <div>
                          <Label htmlFor="guestPassword">Password *</Label>
                          <Input
                            id="guestPassword"
                            type="password"
                            value={guestPassword}
                            onChange={(e) => setGuestPassword(e.target.value)}
                            placeholder="Min 6 characters"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Options (disabled until valid State + City) */}
          {deliveryState && deliveryCity && checkoutSellerId && (
            <DeliveryOptions
              sellerId={checkoutSellerId}
              sellerState={sellerState}
              destinationState={deliveryState}
              destinationCity={deliveryCity}
              items={checkoutItems.map(item => ({
                quantity: item.quantity,
                price: item.price,
                title: item.title
              }))}
              onSelectOption={setSelectedDeliveryOption}
              selectedOptionId={selectedDeliveryOption?.id}
            />
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-8">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Subtotal ({checkoutItems.length} item{checkoutItems.length !== 1 ? 's' : ''})</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    Shipping
                  </span>
                  {selectedDeliveryOption ? (
                    <span>₦{deliveryFee.toLocaleString()}</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">Select location</span>
                  )}
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>VAT ({vatRate}%)</span>
                  <span>₦{Math.round(vat).toLocaleString()}</span>
                </div>
                {!isPayOnDelivery && (
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>Transaction Fee (1.5%)</span>
                    <span>₦{transactionFee.toLocaleString()}</span>
                  </div>
                )}
                {selectedDeliveryOption && (
                  <div className="text-xs text-muted-foreground">
                    {selectedDeliveryOption.name} • {selectedDeliveryOption.estimatedDays}
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-medium text-base sm:text-lg">
                  <span>Total</span>
                  <span>₦{Math.round(grandTotal).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Fez validation warning */}
              {selectedDeliveryOption?.provider === 'fez' && (!recipientName.trim() || !recipientPhone.trim() || !deliveryAddress.trim() || !deliveryState || !deliveryCity) && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span>Please complete delivery details for Fez shipping</span>
                </div>
              )}

              {/* Checkout progress indicator */}
              <div className="space-y-2 mb-2">
                {[
                  { label: 'Delivery details', done: !!(recipientName.trim() && recipientPhone.trim() && deliveryAddress.trim() && deliveryState && deliveryCity) },
                  { label: 'Delivery option', done: !!selectedDeliveryOption },
                  ...(isLoggedIn === false && !isPayOnDelivery ? [{ label: 'Account details', done: !!(guestEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail) && guestPassword && guestPassword.length >= 6) }] : []),
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className={`h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-primary text-primary-foreground' : 'border-2 border-muted-foreground/30'}`}>
                      {step.done && <span className="text-[10px]">✓</span>}
                    </div>
                    <span className={step.done ? 'text-foreground' : 'text-muted-foreground'}>{step.label}</span>
                  </div>
                ))}
              </div>
              
              <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />

              <Button 
                onClick={handleCheckout}
                className="w-full"
                size="lg"
                disabled={processing || !checkoutSellerId || unresolvedSellerItemsCount > 0 || !selectedDeliveryOption || !recipientName.trim() || !recipientPhone.trim() || !deliveryAddress.trim() || !deliveryState || !deliveryCity || (!isPayOnDelivery && isLoggedIn === false && (!guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail) || !guestPassword || guestPassword.length < 6))}
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isPayOnDelivery ? 'Placing order...' : 'Connecting to payment...'}
                  </>
                ) : !recipientName.trim() || !recipientPhone.trim() || !deliveryAddress.trim() || !deliveryState || !deliveryCity ? (
                  'Complete Delivery Details'
                ) : !selectedDeliveryOption ? (
                  'Select Delivery Option'
                ) : isPayOnDelivery ? (
                  `Place Order • Pay ₦${Math.round(grandTotal).toLocaleString()} on Delivery`
                ) : (
                  `Pay ₦${Math.round(grandTotal).toLocaleString()}`
                )}
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/products')}
                className="w-full"
              >
                Continue Shopping
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
